import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Calendar, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

var groupColors = [
  { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', btn: 'bg-green-600 hover:bg-green-700' },
  { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700' },
  { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' },
  { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', btn: 'bg-orange-600 hover:bg-orange-700' },
  { bg: 'bg-pink-50', border: 'border-pink-200', icon: 'text-pink-600', btn: 'bg-pink-600 hover:bg-pink-700' },
];

export default function AdminCourses() {
  var [courses, setCourses] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingCourse, setEditingCourse] = useState(null);
  var [saving, setSaving] = useState(false);

  var [formTitle, setFormTitle] = useState('');
  var [formDesc, setFormDesc] = useState('');
  var [formAudience, setFormAudience] = useState('');
  var [formMethod, setFormMethod] = useState('');
  var [formPrice, setFormPrice] = useState('');
  var [formPriceInfo, setFormPriceInfo] = useState('');
  var [formLocation, setFormLocation] = useState('');
  var [formActive, setFormActive] = useState(true);

  var [groups, setGroups] = useState([{ name: 'Termine', dates: [], time: { start: '09:00', end: '10:50' } }]);

  var { getAuthHeader } = useAuth();

  useEffect(function() { fetchCourses(); }, []);

  function fetchCourses() {
    axios.get(API + '/courses')
      .then(function(res) { setCourses(res.data); })
      .catch(function() { toast.error('Fehler beim Laden'); })
      .finally(function() { setLoading(false); });
  }

  function openCreate() {
    setEditingCourse(null);
    setFormTitle(''); setFormDesc(''); setFormAudience(''); setFormMethod('');
    setFormPrice(''); setFormPriceInfo(''); setFormLocation(''); setFormActive(true);
    setGroups([{ name: 'Termine', dates: [], time: { start: '09:00', end: '10:50' } }]);
    setDialogOpen(true);
  }

  function openEdit(c) {
    setEditingCourse(c);
    setFormTitle(c.title || '');
    setFormDesc(c.description || '');
    setFormAudience(c.target_audience || '');
    setFormMethod(c.method || '');
    setFormPrice(c.price || '');
    setFormPriceInfo(c.price_info || '');
    setFormLocation(c.location || '');
    setFormActive(c.is_active);

    // Load groups from course data
    if (c.groups && c.groups.length > 0) {
      // Add time to each group if not present
      var groupsWithTime = c.groups.map(function(g) {
        var firstDate = g.dates && g.dates[0];
        var time = g.time || {
          start: firstDate ? (firstDate.time_start || '09:00') : '09:00',
          end: firstDate ? (firstDate.time_end || '10:50') : '10:50'
        };
        return Object.assign({}, g, { time: time });
      });
      setGroups(groupsWithTime);
    } else if (c.dates && c.dates.length > 0) {
      // Legacy: convert flat dates array to single group
      var firstDate = c.dates[0];
      setGroups([{
        name: 'Termine',
        dates: c.dates,
        time: { start: firstDate.time_start || '09:00', end: firstDate.time_end || '10:50' }
      }]);
    } else {
      setGroups([{ name: 'Termine', dates: [], time: { start: '09:00', end: '10:50' } }]);
    }

    setDialogOpen(true);
  }

  function addGroup() {
    var newGroupNum = groups.length + 1;
    setGroups([].concat(groups, [{ name: 'Gruppe ' + newGroupNum, dates: [], time: { start: '09:00', end: '10:50' } }]));
  }

  function removeGroup(groupIndex) {
    if (groups.length <= 1) {
      toast.error('Mindestens eine Gruppe erforderlich');
      return;
    }
    setGroups(groups.filter(function(_, i) { return i !== groupIndex; }));
  }

  function updateGroupName(groupIndex, name) {
    var newGroups = groups.map(function(g, i) {
      if (i === groupIndex) {
        return Object.assign({}, g, { name: name });
      }
      return g;
    });
    setGroups(newGroups);
  }

  function addDateToGroup(groupIndex) {
    var groupTime = groups[groupIndex].time || { start: '09:00', end: '10:50' };
    var newDate = { date: '', time_start: groupTime.start, time_end: groupTime.end };
    var newGroups = groups.map(function(g, i) {
      if (i === groupIndex) {
        return Object.assign({}, g, { dates: [].concat(g.dates, [newDate]) });
      }
      return g;
    });
    setGroups(newGroups);
  }

  function removeDateFromGroup(groupIndex, dateIndex) {
    var newGroups = groups.map(function(g, i) {
      if (i === groupIndex) {
        return Object.assign({}, g, { dates: g.dates.filter(function(_, di) { return di !== dateIndex; }) });
      }
      return g;
    });
    setGroups(newGroups);
  }

  function updateDateInGroup(groupIndex, dateIndex, field, value) {
    var newGroups = groups.map(function(g, i) {
      if (i === groupIndex) {
        var newDates = g.dates.map(function(d, di) {
          if (di === dateIndex) {
            var u = Object.assign({}, d);
            u[field] = value;
            return u;
          }
          return d;
        });
        return Object.assign({}, g, { dates: newDates });
      }
      return g;
    });
    setGroups(newGroups);
  }

  function updateGroupTime(groupIndex, field, value) {
    var newGroups = groups.map(function(g, i) {
      if (i === groupIndex) {
        var newTime = Object.assign({}, g.time, { [field]: value });
        return Object.assign({}, g, { time: newTime });
      }
      return g;
    });
    setGroups(newGroups);
  }

  function applyTimeToGroup(groupIndex) {
    var newGroups = groups.map(function(g, i) {
      if (i === groupIndex) {
        var newDates = g.dates.map(function(d) {
          return Object.assign({}, d, { time_start: g.time.start, time_end: g.time.end });
        });
        return Object.assign({}, g, { dates: newDates });
      }
      return g;
    });
    setGroups(newGroups);
    toast.success('Uhrzeit auf alle Termine der Gruppe angewendet');
  }

  function save() {
    if (!formTitle) { toast.error('Bitte Kurstitel eingeben'); return; }
    setSaving(true);

    // Filter out empty dates and prepare groups (including time per group)
    var cleanGroups = groups.map(function(g) {
      return {
        name: g.name,
        time: g.time || { start: '09:00', end: '10:50' },
        dates: g.dates.filter(function(d) { return d.date; })
      };
    }).filter(function(g) { return g.dates.length > 0 || groups.length === 1; });

    // Also create flat dates array for backwards compatibility
    var allDates = cleanGroups.flatMap(function(g) {
      return g.dates.map(function(d) {
        return Object.assign({}, d, { group: g.name });
      });
    });

    // Generate group_name summary
    var groupName = cleanGroups.length === 1 ? cleanGroups[0].name :
      cleanGroups.map(function(g) { return g.name; }).join(' & ');

    var data = {
      title: formTitle,
      description: formDesc,
      target_audience: formAudience,
      method: formMethod,
      price: formPrice,
      price_info: formPriceInfo,
      location: formLocation,
      group_name: groupName,
      is_active: formActive,
      dates: allDates,
      groups: cleanGroups
    };

    var request;
    if (editingCourse) {
      request = axios.put(API + '/courses/' + editingCourse.id, data, getAuthHeader());
    } else {
      request = axios.post(API + '/courses', data, getAuthHeader());
    }

    request
      .then(function() {
        toast.success('Gespeichert!');
        fetchCourses();
        setDialogOpen(false);
      })
      .catch(function() { toast.error('Fehler beim Speichern'); })
      .finally(function() { setSaving(false); });
  }

  function del(id) {
    if (!window.confirm('Kurs wirklich loeschen?')) return;
    axios.delete(API + '/courses/' + id, getAuthHeader())
      .then(function() { toast.success('Geloescht'); fetchCourses(); })
      .catch(function() { toast.error('Fehler'); });
  }

  function toggle(c) {
    axios.put(API + '/courses/' + c.id, { is_active: !c.is_active }, getAuthHeader())
      .then(function() { fetchCourses(); })
      .catch(function() { toast.error('Fehler'); });
  }

  function getTotalDates(c) {
    if (c.groups) {
      return c.groups.reduce(function(sum, g) { return sum + (g.dates ? g.dates.length : 0); }, 0);
    }
    return c.dates ? c.dates.length : 0;
  }

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="spinner" /></div>);
  }

  return (
    <div data-testid="admin-courses-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Kurse und Termine</h1>
          <p className="text-gray-500 mt-2">Verwalten Sie Ihre Kursangebote</p>
        </div>
        <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 text-white">
          <Plus className="h-4 w-4 mr-2" />Neuer Kurs
        </Button>
      </div>

      <Card className="bg-white">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-gray-700">Kurs</TableHead>
                <TableHead className="text-gray-700">Termine</TableHead>
                <TableHead className="text-gray-700">Preis</TableHead>
                <TableHead className="text-gray-700">Status</TableHead>
                <TableHead className="text-center text-gray-700">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {courses.map(function(c) {
                return (
                  <TableRow key={c.id} className="bg-white">
                    <TableCell>
                      <p className="font-medium text-gray-800">{c.title}</p>
                      <p className="text-sm text-gray-500">{c.group_name || c.location}</p>
                    </TableCell>
                    <TableCell className="text-gray-700">{getTotalDates(c)} Termine</TableCell>
                    <TableCell className="text-gray-700">{c.price}</TableCell>
                    <TableCell>
                      <Badge className={c.is_active ? 'bg-teal-600 cursor-pointer text-white hover:bg-teal-700' : 'bg-gray-200 text-gray-700 cursor-pointer hover:bg-gray-300'} onClick={function() { toggle(c); }}>
                        {c.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="outline" onClick={function() { openEdit(c); }} className="mr-2 text-gray-700 border-gray-300">
                        <Edit className="h-4 w-4 mr-1" />Bearbeiten
                      </Button>
                      <Button size="sm" variant="ghost" onClick={function() { del(c.id); }} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {courses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    Noch keine Kurse vorhanden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-800">{editingCourse ? 'Kurs bearbeiten' : 'Neuer Kurs'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-4">Kursinformationen</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Kursname *</Label>
                  <Input value={formTitle} onChange={function(e) { setFormTitle(e.target.value); }} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="z.B. HypnoMentale Geburtsvorbereitung" />
                </div>
                <div>
                  <Label className="text-gray-700">Preis</Label>
                  <Input value={formPrice} onChange={function(e) { setFormPrice(e.target.value); }} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="z.B. 134,06 Euro pro Termin" />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-gray-700">Beschreibung</Label>
                <Textarea value={formDesc} onChange={function(e) { setFormDesc(e.target.value); }} rows={3} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="Beschreibung des Kurses..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-gray-700">Zielgruppe</Label>
                  <Input value={formAudience} onChange={function(e) { setFormAudience(e.target.value); }} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="z.B. Schwangere ab der 20. SSW" />
                </div>
                <div>
                  <Label className="text-gray-700">Methode</Label>
                  <Input value={formMethod} onChange={function(e) { setFormMethod(e.target.value); }} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="z.B. Tiefenentspannung" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-gray-700">Ort</Label>
                  <Input value={formLocation} onChange={function(e) { setFormLocation(e.target.value); }} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="z.B. Praxis Borken" />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-3">
                    <Switch checked={formActive} onCheckedChange={setFormActive} />
                    <Label className="text-gray-700">Kurs aktiv</Label>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-gray-700">Preis-Hinweis</Label>
                <Textarea value={formPriceInfo} onChange={function(e) { setFormPriceInfo(e.target.value); }} rows={2} className="mt-1 bg-white text-gray-800 border-gray-300" placeholder="z.B. Abrechnung gemaess GOP..." />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Users className="h-5 w-5 text-teal-600" />
                  Termine / Gruppen
                </h3>
                <Button onClick={addGroup} size="sm" variant="outline" className="text-teal-700 border-teal-300">
                  <Plus className="h-4 w-4 mr-1" />Gruppe hinzufuegen
                </Button>
              </div>

              <div className={groups.length > 1 ? "grid grid-cols-1 md:grid-cols-2 gap-4" : ""}>
                {groups.map(function(group, groupIndex) {
                  var colors = groupColors[groupIndex % groupColors.length];
                  return (
                    <div key={groupIndex} className={"p-4 rounded-lg border " + colors.bg + " " + colors.border}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-1">
                          <Users className={"h-5 w-5 " + colors.icon} />
                          <Input
                            value={group.name}
                            onChange={function(e) { updateGroupName(groupIndex, e.target.value); }}
                            className="bg-white text-gray-800 border-gray-300 font-semibold max-w-[150px]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button onClick={function() { addDateToGroup(groupIndex); }} size="sm" className={colors.btn + " text-white"}>
                            <Plus className="h-4 w-4 mr-1" />Termin
                          </Button>
                          {groups.length > 1 && (
                            <Button onClick={function() { removeGroup(groupIndex); }} size="sm" variant="ghost" className="text-red-500 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-3 p-2 bg-white rounded border border-gray-200">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Uhrzeit:</span>
                        <Input type="time" value={group.time ? group.time.start : '09:00'} onChange={function(e) { updateGroupTime(groupIndex, 'start', e.target.value); }} className="w-28 bg-white text-gray-800 border-gray-300 text-sm" />
                        <span className="text-gray-500">-</span>
                        <Input type="time" value={group.time ? group.time.end : '10:50'} onChange={function(e) { updateGroupTime(groupIndex, 'end', e.target.value); }} className="w-28 bg-white text-gray-800 border-gray-300 text-sm" />
                        {group.dates.length > 0 && (
                          <Button onClick={function() { applyTimeToGroup(groupIndex); }} size="sm" variant="ghost" className="text-xs text-gray-600 hover:text-gray-800">
                            Auf alle
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {group.dates.map(function(d, dateIndex) {
                          return (
                            <div key={dateIndex} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                              <span className="text-sm font-medium text-gray-500 w-6">#{dateIndex + 1}</span>
                              <Input type="date" value={d.date} onChange={function(e) { updateDateInGroup(groupIndex, dateIndex, 'date', e.target.value); }} className="flex-1 bg-white text-gray-800 border-gray-300" />
                              <Button variant="ghost" size="sm" onClick={function() { removeDateFromGroup(groupIndex, dateIndex); }} className="text-red-500 hover:bg-red-50 p-1">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                        {group.dates.length === 0 && <p className="text-center text-gray-500 py-4 text-sm">Noch keine Termine</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={function() { setDialogOpen(false); }} className="text-gray-700 border-gray-300">Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
